import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { skeletonVariants } from './skeleton.variants.ts'

/**
 * Loading placeholder. `variant` picks a DS-aligned geometry preset; `width` / `height` / `radius`
 * are fine-grained overrides that win over the preset (inline style beats class).
 *
 * Accessibility is owned by the surrounding loading *region* (`aria-busy`), not by each skeleton.
 *
 * @summary Pulsing loading placeholder with `block` / `text` / `button` presets.
 */
function Skeleton({ variant, width, height, radius, className, style, ...props }: Skeleton.Props) {
  return (
    <div
      data-slot="skeleton"
      className={cn(skeletonVariants({ variant }), className)}
      style={{ width, height, borderRadius: radius, ...style }}
      {...props}
    />
  )
}

namespace Skeleton {
  type Variants = NonNullable<VariantProps<typeof skeletonVariants>>
  export type Props = React.ComponentProps<'div'> & {
    /** DS-aligned geometry preset. @default 'block' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Width of the placeholder. `number` → px, or any CSS length. Overrides the preset. @default auto */
    width?: number | string
    /** Height of the placeholder. `number` → px, or any CSS length. Overrides the preset. @default auto */
    height?: number | string
    /** Border radius. `number` → px, or any CSS length. Overrides the preset. @default per variant */
    radius?: number | string
  }
}

export { Skeleton, skeletonVariants }
